'use client';

import Link from 'next/link';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useState, useEffect } from 'react';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.neotral.org';

interface CoverageItem {
  source_id: string;
  source_name: string | null;
  article_count: number;
  percentage: number;
  first_seen: string;
  last_seen: string;
}

interface TrendingTopic {
  topic: string;
  total_count: number;
  source_count: number;
}

// Source display names
const SOURCE_NAMES: Record<string, string> = {
  elpais: 'El País',
  elmundo: 'El Mundo',
  abc: 'ABC',
  lavanguardia: 'La Vanguardia',
  eldiario: 'El Diario',
  elespanol: 'El Español',
  libertaddigital: 'Libertad Digital',
  okdiario: 'OK Diario',
  elconfidencial: 'El Confidencial',
  '20minutos': '20 Minutos',
  publico: 'Público',
  larazon: 'La Razón',
  cope: 'COPE',
  elperiodico: 'El Periódico',
};

// Source colors for bars
const SOURCE_COLORS: Record<string, string> = {
  elpais: 'bg-blue-600',
  elmundo: 'bg-sky-500',
  abc: 'bg-orange-500',
  lavanguardia: 'bg-purple-600',
  eldiario: 'bg-green-600',
  elespanol: 'bg-red-500',
  libertaddigital: 'bg-amber-600',
  okdiario: 'bg-rose-600',
  elconfidencial: 'bg-slate-600',
  '20minutos': 'bg-yellow-500',
  publico: 'bg-red-700',
  larazon: 'bg-blue-800',
  cope: 'bg-cyan-600',
  elperiodico: 'bg-emerald-600',
};

// Pre-populated trending topics (fallback when API returns empty)
const SUGGESTED_TOPICS = {
  politicos: [
    { topic: 'Sánchez', emoji: '🔴' },
    { topic: 'Feijóo', emoji: '🔵' },
    { topic: 'Abascal', emoji: '🟢' },
    { topic: 'Yolanda Díaz', emoji: '🟣' },
    { topic: 'Ayuso', emoji: '🔵' },
  ],
  temas: [
    { topic: 'DANA', emoji: '🌊' },
    { topic: 'inmigración', emoji: '🚢' },
    { topic: 'vivienda', emoji: '🏠' },
    { topic: 'pensiones', emoji: '👴' },
    { topic: 'sanidad', emoji: '🏥' },
    { topic: 'educación', emoji: '📚' },
    { topic: 'economía', emoji: '📈' },
    { topic: 'inflación', emoji: '💸' },
  ],
  conflictos: [
    { topic: 'Cataluña', emoji: '🏴' },
    { topic: 'ETA', emoji: '⚠️' },
    { topic: 'amnistía', emoji: '⚖️' },
    { topic: 'Puigdemont', emoji: '🏃' },
  ],
  actualidad: [
    { topic: 'Ucrania', emoji: '🇺🇦' },
    { topic: 'Trump', emoji: '🇺🇸' },
    { topic: 'Gaza', emoji: '🕊️' },
    { topic: 'inteligencia artificial', emoji: '🤖' },
    { topic: 'corrupción', emoji: '💰' },
  ],
};

export default function RadarPage() {
  const today = format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: es });
  const todayCapitalized = today.charAt(0).toUpperCase() + today.slice(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [coverage, setCoverage] = useState<CoverageItem[]>([]);
  const [trending, setTrending] = useState<TrendingTopic[]>([]);
  const [totalArticles, setTotalArticles] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Load trending topics on mount
  useEffect(() => {
    fetch(`${API_URL}/api/radar`)
      .then(res => res.json())
      .then(data => {
        if (data.trending) {
          setTrending(data.trending);
        }
      })
      .catch(console.error);
  }, []);

  const handleSearch = async (topic?: string) => {
    const query = topic || searchTerm;
    if (!query.trim()) return;

    setLoading(true);
    setSearched(true);

    try {
      const res = await fetch(`${API_URL}/api/radar?topic=${encodeURIComponent(query)}`);
      const data = await res.json();
      
      if (data.success) {
        setCoverage(data.coverage || []);
        setTotalArticles(data.total_articles || 0);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleTrendingClick = (topic: string) => {
    setSearchTerm(topic);
    handleSearch(topic);
  };

  return (
    <div className="min-h-screen bg-[#FDF6E3]">
      {/* Header */}
      <header className="border-b-2 border-stone-800 bg-[#FDF6E3] sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          {/* Top bar: Date */}
          <div className="flex items-center justify-between py-2 border-b border-stone-200">
            <span className="text-xs text-stone-500 font-mono uppercase tracking-wider">
              {todayCapitalized}
            </span>
            <span className="text-xs text-stone-400">España</span>
          </div>
          
          {/* Masthead */}
          <div className="text-center py-4">
            <Link href="/">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-black tracking-tight text-stone-900 hover:text-stone-700 transition-colors">
                NEOTRAL
              </h1>
            </Link>
            <p className="text-xs sm:text-sm text-stone-500 italic mt-1 font-serif">
              Todas las versiones. Tú decides.
            </p>
          </div>
          
          {/* Navigation */}
          <nav className="flex justify-center gap-6 sm:gap-8 py-2 border-t border-stone-200 text-sm overflow-x-auto no-scrollbar">
            <Link href="/" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Hoy
            </Link>
            <Link href="/radar" className="font-serif font-semibold text-stone-900 whitespace-nowrap">
              Radar
            </Link>
            <Link href="/medios" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Medios
            </Link>
            <Link href="/silenciadas" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Silenciadas
            </Link>
            <Link href="/algoritmo" className="font-serif text-stone-500 hover:text-stone-900 transition-colors whitespace-nowrap">
              Algoritmo
            </Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <h2 className="text-3xl sm:text-4xl font-serif font-bold text-stone-900 mb-3">
          📡 Radar de Cobertura
        </h2>
        
        <p className="text-lg text-stone-600 mb-8" style={{ fontFamily: 'Georgia, serif' }}>
          Compara cómo diferentes medios cubren los mismos temas a lo largo del tiempo.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Search */}
          <div className="border border-paper-200 p-6">
            <h4 className="font-semibold mb-3">Buscar tema</h4>
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Ej: Sánchez, Feijóo, ETA..."
                className="flex-1 border border-paper-300 px-4 py-2 text-sm focus:outline-none focus:border-ink-900"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <button 
                onClick={() => handleSearch()}
                disabled={loading}
                className="bg-ink-900 text-white px-4 py-2 text-sm hover:bg-ink-700 disabled:opacity-50"
              >
                {loading ? '...' : 'Buscar'}
              </button>
            </div>
          </div>
          
          {/* Trending */}
          <div className="border border-paper-200 p-6">
            <h4 className="font-semibold mb-3">Temas trending</h4>
            {trending.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {trending.slice(0, 8).map((t, i) => (
                  <button
                    key={i}
                    onClick={() => handleTrendingClick(t.topic)}
                    className="text-sm px-3 py-1 bg-paper-100 hover:bg-paper-200 border border-paper-300 rounded-full"
                  >
                    {t.topic} <span className="text-ink-400">({t.total_count})</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-ink-400">Cargando temas...</p>
            )}
          </div>
        </div>

        {/* Suggested Topics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Políticos */}
          <div className="border border-paper-200 p-4">
            <h5 className="text-xs uppercase tracking-wider text-ink-500 mb-3 font-semibold">👥 Políticos</h5>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_TOPICS.politicos.map((item, i) => (
                <button
                  key={i}
                  onClick={() => handleTrendingClick(item.topic)}
                  className="text-sm px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-full transition-colors"
                >
                  {item.emoji} {item.topic}
                </button>
              ))}
            </div>
          </div>

          {/* Temas */}
          <div className="border border-paper-200 p-4">
            <h5 className="text-xs uppercase tracking-wider text-ink-500 mb-3 font-semibold">📋 Temas clave</h5>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_TOPICS.temas.map((item, i) => (
                <button
                  key={i}
                  onClick={() => handleTrendingClick(item.topic)}
                  className="text-sm px-3 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full transition-colors"
                >
                  {item.emoji} {item.topic}
                </button>
              ))}
            </div>
          </div>

          {/* Conflictos */}
          <div className="border border-paper-200 p-4">
            <h5 className="text-xs uppercase tracking-wider text-ink-500 mb-3 font-semibold">⚡ Conflictos</h5>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_TOPICS.conflictos.map((item, i) => (
                <button
                  key={i}
                  onClick={() => handleTrendingClick(item.topic)}
                  className="text-sm px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 rounded-full transition-colors"
                >
                  {item.emoji} {item.topic}
                </button>
              ))}
            </div>
          </div>

          {/* Actualidad */}
          <div className="border border-paper-200 p-4">
            <h5 className="text-xs uppercase tracking-wider text-ink-500 mb-3 font-semibold">🌍 Actualidad</h5>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_TOPICS.actualidad.map((item, i) => (
                <button
                  key={i}
                  onClick={() => handleTrendingClick(item.topic)}
                  className="text-sm px-3 py-1.5 bg-green-50 hover:bg-green-100 border border-green-200 rounded-full transition-colors"
                >
                  {item.emoji} {item.topic}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        {searched && (
          <div className="border border-paper-200 p-6">
            <h3 className="font-semibold mb-4">
              {loading ? 'Buscando...' : (
                coverage.length > 0 
                  ? `Cobertura de "${searchTerm}" (${totalArticles} artículos)`
                  : `No se encontraron resultados para "${searchTerm}"`
              )}
            </h3>
            
            {coverage.length > 0 && (
              <div className="space-y-3">
                {coverage.map((item, i) => {
                  const sourceName = SOURCE_NAMES[item.source_id] || item.source_id;
                  const barColor = SOURCE_COLORS[item.source_id] || 'bg-gray-500';
                  
                  return (
                    <div key={i} className="flex items-center gap-4">
                      <div className="w-32 text-sm font-medium truncate">
                        {sourceName}
                      </div>
                      <div className="flex-1 bg-paper-100 h-6 relative">
                        <div 
                          className={`${barColor} h-full transition-all duration-500`}
                          style={{ width: `${Math.max(item.percentage, 2)}%` }}
                        />
                        <span className="absolute right-2 top-0 h-full flex items-center text-xs text-ink-600">
                          {item.article_count} ({item.percentage}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {coverage.length > 0 && (
              <p className="text-xs text-ink-400 mt-4">
                Datos de los últimos 30 días
              </p>
            )}
          </div>
        )}

        {/* Info box if not searched */}
        {!searched && (
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
            <h3 className="font-semibold text-blue-800 mb-2">💡 Cómo usar el Radar</h3>
            <p className="text-blue-900 text-sm">
              Escribe un tema, nombre o palabra clave para ver qué medios lo cubren más y cuáles lo ignoran.
              Ideal para detectar silencios informativos y comparar atención mediática.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-ink-900 mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 text-center">
          <p className="text-xs text-ink-400">
            © {new Date().getFullYear()} Neotral · Fundación MAFT
          </p>
        </div>
      </footer>
    </div>
  );
}
